import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Upload, FileSpreadsheet, CheckCircle, AlertCircle, XCircle,
  Check, X as XIcon, ChevronRight, ChevronLeft, ArrowRight,
  Table2, Columns3, Eye, Play, Save, Trash2, RotateCcw, Loader2, BookmarkPlus,
  Clock, Download, History, User, RefreshCw
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import {
  analyzeImportFile, previewImport, executeImport,
  getReviewQueue, approveProduct, rejectProduct,
  getLatestPendingCount, approveAllLatestBatch,
  getMappingPresets, saveMappingPreset, deleteMappingPreset,
  getImportHistory, deleteImportHistory, getImportFileDownloadUrl, getImportErrorReportUrl
} from '../../lib/api';
import { Toaster, toast } from 'sonner';

const CATEGORY_PRESETS = ['NBK', 'PC', 'Apple', 'AIO', 'LCD', 'Workstations'];

// --- Step indicator ---
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="step-indicator">
    {steps.map((step, i) => {
      const Icon = step.icon;
      const isActive = i === currentStep;
      const isDone = i < currentStep;
      return (
        <React.Fragment key={step.id}>
          {i > 0 && (
            <div className={`h-px w-8 shrink-0 ${isDone ? 'bg-[#00D4FF]' : 'bg-white/10'}`} />
          )}
          <div
            className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider shrink-0 transition-colors ${
              isActive ? 'text-[#00D4FF] bg-[#00D4FF]/10 border border-[#00D4FF]/30' :
              isDone ? 'text-[#10B981]' : 'text-white/30'
            }`}
            data-testid={`step-${step.id}`}
          >
            {isDone ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

// --- Step 1: Upload ---
const UploadStep = ({ onFileSelect, file, t }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) validateAndSelect(dropped);
  };

  const validateAndSelect = (f) => {
    const ext = f.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error(t('adminImport.unsupportedFormat'));
      return;
    }
    onFileSelect(f);
  };

  return (
    <div data-testid="upload-step">
      <h2 className="text-xl font-semibold mb-2">{t('adminImport.uploadTitle')}</h2>
      <p className="text-white/50 text-sm mb-6">{t('adminImport.uploadDesc')}</p>
      <div
        className={`border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-[#00D4FF] bg-[#00D4FF]/5' : 'border-white/10 hover:border-[#00D4FF]/30'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid="file-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => e.target.files?.[0] && validateAndSelect(e.target.files[0])}
          className="hidden"
          data-testid="file-input"
        />
        <FileSpreadsheet className="w-14 h-14 text-white/15 mx-auto mb-4" />
        {file ? (
          <div>
            <p className="text-white font-medium text-lg">{file.name}</p>
            <p className="text-white/40 text-sm mt-1">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        ) : (
          <div>
            <p className="text-white/60 mb-1">{t('adminImport.dropOrBrowse')}</p>
            <p className="text-white/30 text-xs">{t('adminImport.anyColumnLayout')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Step 2: Sheet selection ---
const SheetStep = ({ sheets, selectedSheet, onSelect, recommended, ignoredSheets, sheetNotes, t }) => (
  <div data-testid="sheet-step">
    <h2 className="text-xl font-semibold mb-2">{t('adminImport.selectSheetTitle')}</h2>
    <p className="text-white/50 text-sm mb-4">{t('adminImport.selectSheetDesc')}</p>

    {/* Sheet notes (auto-selection info) */}
    {sheetNotes?.length > 0 && (
      <div className="mb-4 space-y-1" data-testid="sheet-notes">
        {sheetNotes.map((note, i) => (
          <div key={i} className="flex items-center gap-2 text-xs px-3 py-2 bg-[#00D4FF]/5 border border-[#00D4FF]/20 text-[#00D4FF]">
            <CheckCircle className="w-3.5 h-3.5 shrink-0" />
            <span>{note}</span>
          </div>
        ))}
      </div>
    )}

    {/* Ignored sheets */}
    {ignoredSheets?.length > 0 && (
      <div className="mb-4" data-testid="ignored-sheets">
        {ignoredSheets.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-xs px-3 py-2 bg-white/5 border border-white/10 text-white/40">
            <XCircle className="w-3.5 h-3.5 shrink-0 text-white/30" />
            <span>Skipped "{s.name}" — {s.reason}</span>
          </div>
        ))}
      </div>
    )}

    <div className="space-y-3">
      {sheets.map((s) => (
        <button
          key={s.name}
          onClick={() => onSelect(s.name)}
          className={`w-full text-left p-4 border transition-colors flex items-center justify-between group ${
            selectedSheet === s.name
              ? 'border-[#00D4FF] bg-[#00D4FF]/10'
              : 'border-white/10 hover:border-white/20 bg-[#050505]'
          }`}
          data-testid={`sheet-${s.name}`}
        >
          <div className="flex items-center gap-3">
            <Table2 className={`w-5 h-5 ${selectedSheet === s.name ? 'text-[#00D4FF]' : 'text-white/30'}`} />
            <div>
              <span className="font-medium">{s.name}</span>
              {s.name === recommended && (
                <span className="ml-2 text-[10px] uppercase tracking-widest bg-[#00D4FF]/20 text-[#00D4FF] px-2 py-0.5">
                  {t('adminImport.recommended')}
                </span>
              )}
            </div>
          </div>
          <div className="text-white/40 text-sm font-mono">
            {s.row_count} {t('adminImport.rows')} / {s.columns.length} cols
          </div>
        </button>
      ))}
    </div>
  </div>
);

// --- Step 3: Column Mapping ---
const MappingStep = ({ columns, systemFields, mappings, onMappingsChange, presets, onSavePreset, onLoadPreset, onDeletePreset, importCategory, onCategoryChange, t }) => {
  const [presetName, setPresetName] = useState('');
  const [showPresetSave, setShowPresetSave] = useState(false);

  const updateMapping = (targetField, sourceColumn) => {
    const newMappings = { ...mappings };
    if (!sourceColumn) {
      delete newMappings[targetField];
    } else {
      for (const key of Object.keys(newMappings)) {
        if (newMappings[key] === sourceColumn && key !== targetField) {
          delete newMappings[key];
        }
      }
      newMappings[targetField] = sourceColumn;
    }
    onMappingsChange(newMappings);
  };

  const mappedSources = new Set(Object.values(mappings));

  return (
    <div data-testid="mapping-step">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-semibold">{t('adminImport.mapColumnsTitle')}</h2>
      </div>
      <p className="text-white/50 text-sm mb-4">{t('adminImport.mapColumnsDesc')}</p>

      {/* Category selector — choose before mapping so LCD fields are de-emphasized */}
      <div className="mb-4 p-3 bg-[#050505] border border-white/10 flex items-center gap-4" data-testid="category-selector-mapping">
        <label className="text-sm text-white/60 shrink-0">{t('adminImport.tagCategory')}</label>
        <select value={importCategory} onChange={(e) => onCategoryChange(e.target.value)}
          className="text-sm bg-black/50 border border-white/10 text-white px-3 py-2 h-auto" data-testid="import-category-select-mapping">
          <option value="">{t('adminImport.selectCategory')}</option>
          {CATEGORY_PRESETS.map((c) => (<option key={c} value={c}>{c}</option>))}
        </select>
        <span className="text-xs text-white/30">{t('adminImport.categoryTrackingHint')}</span>
      </div>

      {/* Preset controls */}
      <div className="flex flex-wrap items-center gap-2 mb-6 p-3 bg-[#050505] border border-white/10">
        <span className="text-xs text-white/40 uppercase tracking-wider mr-1">{t('adminImport.presetsLabel')}</span>
        {presets.map((p) => (
          <div key={p.id} className="flex items-center group">
            <button
              onClick={() => onLoadPreset(p)}
              className="text-xs px-3 py-1.5 border border-white/10 text-white/70 hover:text-[#00D4FF] hover:border-[#00D4FF]/30 transition-colors"
              data-testid={`preset-load-${p.name}`}
            >
              {p.name}
            </button>
            <button
              onClick={() => onDeletePreset(p.id)}
              className="text-white/20 hover:text-[#EF4444] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              title={t('common.delete')}
            >
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        ))}
        {!showPresetSave ? (
          <button
            onClick={() => setShowPresetSave(true)}
            className="text-xs px-3 py-1.5 border border-dashed border-white/10 text-white/40 hover:text-[#00D4FF] hover:border-[#00D4FF]/30 transition-colors flex items-center gap-1"
            data-testid="save-preset-toggle"
          >
            <BookmarkPlus className="w-3 h-3" /> {t('adminImport.saveCurrent')}
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <select
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              className="text-xs bg-black/50 border border-white/10 text-white px-2 py-1.5 h-auto"
              data-testid="preset-name-select"
            >
              <option value="">{t('adminImport.selectType')}</option>
              {CATEGORY_PRESETS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button
              onClick={() => {
                if (presetName) { onSavePreset(presetName); setShowPresetSave(false); setPresetName(''); }
              }}
              disabled={!presetName}
              className="text-xs px-2 py-1.5 bg-[#00D4FF] text-black disabled:opacity-30"
              data-testid="save-preset-confirm"
            >
              <Save className="w-3 h-3" />
            </button>
            <button
              onClick={() => { setShowPresetSave(false); setPresetName(''); }}
              className="text-xs px-1 py-1.5 text-white/40 hover:text-white"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* LCD info banner */}
      {importCategory === 'LCD' && (
        <div className="mb-4 flex items-center gap-2 text-xs px-3 py-2 bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-[#F59E0B]" data-testid="lcd-fields-note">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>LCD import — CPU, RAM, Hard Drive, Battery, Adapter fields are not required for monitors</span>
        </div>
      )}

      {/* Mapping table */}
      <div className="space-y-2">
        {systemFields.map((field) => {
          const currentSource = mappings[field.field] || '';
          const isExcluded = importCategory === 'LCD' && field.exclude_categories?.includes('LCD');
          return (
            <div
              key={field.field}
              className={`flex items-center gap-4 p-3 border transition-colors ${
                isExcluded ? 'border-white/5 bg-[#050505] opacity-40' :
                currentSource ? 'border-[#10B981]/30 bg-[#10B981]/5' : field.required ? 'border-[#EF4444]/20 bg-[#EF4444]/5' : 'border-white/5 bg-[#050505]'
              }`}
              data-testid={`mapping-row-${field.field}`}
            >
              <div className="w-1/3 min-w-[140px]">
                <span className={`text-sm ${field.required ? 'text-white font-medium' : 'text-white/70'}`}>
                  {field.label}
                  {field.required && <span className="text-[#EF4444] ml-1">*</span>}
                </span>
                {field.hint && <p className="text-[10px] text-white/30 mt-0.5">{field.hint}</p>}
              </div>
              <ArrowRight className={`w-4 h-4 shrink-0 ${currentSource ? 'text-[#10B981]' : 'text-white/10'}`} />
              <div className="flex-1">
                <select
                  value={currentSource}
                  onChange={(e) => updateMapping(field.field, e.target.value)}
                  className={`w-full text-sm bg-black/50 border px-3 py-2 h-auto ${
                    currentSource ? 'border-[#10B981]/30 text-white' : 'border-white/10 text-white/40'
                  }`}
                  data-testid={`mapping-select-${field.field}`}
                >
                  <option value="">{t('adminImport.notMapped')}</option>
                  {columns.map((col) => (
                    <option
                      key={col}
                      value={col}
                      disabled={mappedSources.has(col) && mappings[field.field] !== col}
                    >
                      {col} {mappedSources.has(col) && mappings[field.field] !== col ? t('adminImport.used') : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-6">
                {currentSource ? (
                  <Check className="w-4 h-4 text-[#10B981]" />
                ) : field.required ? (
                  <AlertCircle className="w-4 h-4 text-[#EF4444]" />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Step 4: Preview ---
const PreviewStep = ({ previewData, totalRows, mappedFields, validation, t }) => {
  const [showErrors, setShowErrors] = useState(false);
  if (!previewData) return null;
  const v = validation;
  const hasErrors = v && v.error_rows > 0;

  return (
    <div data-testid="preview-step">
      <h2 className="text-xl font-semibold mb-2">{t('adminImport.previewTitle')}</h2>
      <p className="text-white/50 text-sm mb-4">
        {previewData.length} / {totalRows} {t('adminImport.rows')}
      </p>

      {/* Validation Summary */}
      {v && (
        <div className={`p-4 mb-6 border ${hasErrors ? 'bg-[#EF4444]/5 border-[#EF4444]/20' : 'bg-[#10B981]/5 border-[#10B981]/20'}`} data-testid="validation-summary">
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-sm font-semibold uppercase tracking-wider ${hasErrors ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>
              {hasErrors ? `Validation: ${v.error_rows} issue${v.error_rows > 1 ? 's' : ''} found` : 'Validation Passed'}
            </h3>
            <span className="text-xs text-white/40">{v.valid_rows} / {v.total_rows} rows valid</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
            <div><span className="text-white/40">Total Rows</span><div className="font-mono font-bold text-lg mt-0.5">{v.total_rows}</div></div>
            <div><span className="text-white/40">Valid</span><div className="font-mono font-bold text-lg mt-0.5 text-[#10B981]">{v.valid_rows}</div></div>
            <div><span className="text-white/40">Errors</span><div className={`font-mono font-bold text-lg mt-0.5 ${v.error_rows > 0 ? 'text-[#EF4444]' : 'text-white/30'}`}>{v.error_rows}</div></div>
            <div><span className="text-white/40">Blank IDs</span><div className={`font-mono font-bold text-lg mt-0.5 ${v.blank_unique_ids > 0 ? 'text-[#F59E0B]' : 'text-white/30'}`}>{v.blank_unique_ids}</div></div>
            <div><span className="text-white/40">Duplicates</span><div className={`font-mono font-bold text-lg mt-0.5 ${v.duplicate_unique_ids > 0 ? 'text-[#F59E0B]' : 'text-white/30'}`}>{v.duplicate_unique_ids}</div></div>
          </div>
          {hasErrors && v.errors?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <button onClick={() => setShowErrors(!showErrors)} className="text-xs text-[#EF4444] hover:text-[#EF4444]/80 flex items-center gap-1" data-testid="toggle-errors-btn">
                {showErrors ? 'Hide' : 'Show'} {v.total_error_count} error{v.total_error_count > 1 ? 's' : ''}
                {v.has_more_errors && ` (showing first 100)`}
              </button>
              {showErrors && (
                <div className="mt-2 max-h-60 overflow-y-auto space-y-1">
                  <div className="grid grid-cols-[60px_1fr_1fr_2fr] gap-2 text-[10px] text-white/30 uppercase font-semibold pb-1 border-b border-white/5">
                    <span>Row</span><span>Column</span><span>Value</span><span>Reason</span>
                  </div>
                  {v.errors.map((e, i) => (
                    <div key={i} className="grid grid-cols-[60px_1fr_1fr_2fr] gap-2 text-xs text-white/60 py-0.5">
                      <span className="font-mono text-white/30">{e.row}</span>
                      <span className="text-white/50 truncate">{e.column}</span>
                      <span className="text-[#F59E0B] truncate font-mono">{e.value || '\u2014'}</span>
                      <span className="text-[#EF4444]">{e.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full table-terminal text-xs">
          <thead>
            <tr>
              <th className="text-left">#</th>
              {mappedFields.includes('unique_id') && <th className="text-left">{t('adminImport.skuCol')}</th>}
              {mappedFields.includes('oem') && <th className="text-left">{t('adminImport.brandCol')}</th>}
              {mappedFields.includes('model') && <th className="text-left">{t('adminImport.modelCol')}</th>}
              {mappedFields.includes('cost_price') && <th className="text-right">{t('adminImport.costCol')}</th>}
              <th className="text-right">{t('adminImport.saleCol')}</th>
              {mappedFields.includes('cpu') && <th className="text-left">CPU</th>}
              {mappedFields.includes('ram') && <th className="text-left">RAM</th>}
              {mappedFields.includes('hard_drive') && <th className="text-left">Storage</th>}
              {mappedFields.includes('grade') && <th className="text-center">{t('adminImport.gradeCol')}</th>}
              <th className="text-left">{t('adminImport.issues')}</th>
            </tr>
          </thead>
          <tbody>
            {previewData.map((row, i) => (
              <tr key={i} className={row.validation_issues?.length > 0 ? 'bg-[#EF4444]/5' : ''}>
                <td className="text-white/30">{row._row}</td>
                {mappedFields.includes('unique_id') && (
                  <td className="font-mono text-[#00D4FF]">{row.unique_id || '\u2014'}</td>
                )}
                {mappedFields.includes('oem') && <td>{row.oem || '\u2014'}</td>}
                {mappedFields.includes('model') && (
                  <td className="max-w-[200px] truncate">{row.model || '\u2014'}</td>
                )}
                {mappedFields.includes('cost_price') && (
                  <td className="text-right font-mono text-white/50">
                    {row.cost_price ? `$${parseFloat(row.cost_price.replace(/[$,]/g, '') || 0).toLocaleString()}` : '\u2014'}
                  </td>
                )}
                <td className="text-right font-mono text-[#00D4FF]">
                  ${row.computed_sale_price?.toLocaleString() || '\u2014'}
                  {row.price_source === 'override' && (
                    <span className="ml-1 text-[8px] text-[#F59E0B] uppercase">ovr</span>
                  )}
                </td>
                {mappedFields.includes('cpu') && <td className="text-white/60 truncate max-w-[120px]">{row.cpu || '\u2014'}</td>}
                {mappedFields.includes('ram') && <td className="text-white/60">{row.ram || '\u2014'}</td>}
                {mappedFields.includes('hard_drive') && <td className="text-white/60">{row.hard_drive || '\u2014'}</td>}
                {mappedFields.includes('grade') && (
                  <td className="text-center">
                    {row.grade ? (
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-bold ${
                        row.grade.toUpperCase() === 'A' ? 'grade-a text-white' :
                        row.grade.toUpperCase() === 'B' ? 'grade-b text-white' :
                        'grade-c text-white'
                      }`}>{row.grade.toUpperCase()}</span>
                    ) : '\u2014'}
                  </td>
                )}
                <td>
                  {row.validation_issues?.length > 0 ? (
                    <span className="text-[#EF4444] text-[10px]">{row.validation_issues.join(', ')}</span>
                  ) : (
                    <Check className="w-3 h-3 text-[#10B981]" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- Step 5: Results ---
const ResultStep = ({ result, t, language }) => {
  const [showErrors, setShowErrors] = useState(false);
  if (!result) return null;
  const ps = result.pricing_summary;

  const handleDownloadErrorReport = () => {
    if (!result.history_id) return;
    const token = localStorage.getItem('luxortek_admin_token');
    const url = getImportErrorReportUrl(result.history_id);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Download failed');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `import_errors_${result.history_id}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert('Failed to download error report'));
  };

  return (
    <div data-testid="result-step">
      <h2 className="text-xl font-semibold mb-6">{t('adminImport.importComplete')}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className="p-6 bg-[#10B981]/10 border border-[#10B981]/30 text-center">
          <CheckCircle className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
          <div className="text-2xl font-bold font-mono text-[#10B981]">{result.imported}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('adminImport.newAdded')}</div>
        </div>
        <div className="p-6 bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-center">
          <RefreshCw className="w-8 h-8 text-[#00D4FF] mx-auto mb-2" />
          <div className="text-2xl font-bold font-mono text-[#00D4FF]">{result.updated || 0}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('adminImport.updated')}</div>
        </div>
        <div className="p-6 bg-[#EF4444]/10 border border-[#EF4444]/30 text-center">
          <XCircle className="w-8 h-8 text-[#EF4444] mx-auto mb-2" />
          <div className="text-2xl font-bold font-mono text-[#EF4444]">{result.errors}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('adminImport.errors')}</div>
        </div>
        <div className="p-6 bg-white/5 border border-white/10 text-center">
          <FileSpreadsheet className="w-8 h-8 text-white/40 mx-auto mb-2" />
          <div className="text-2xl font-bold font-mono">{result.total_rows}</div>
          <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{t('adminImport.totalRows')}</div>
        </div>
      </div>

      {/* Pricing Summary */}
      {ps && (ps.valid_cost_prices > 0 || ps.invalid_cost_prices > 0) && (
        <div className="bg-[#050505] border border-white/10 p-5 mb-6" data-testid="pricing-summary">
          <h3 className="text-sm font-semibold text-white/80 mb-4 uppercase tracking-wider">
            {language === 'es' ? 'Resumen de Precios Importados' : 'Pricing Validation Summary'}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div>
              <div className="text-xs text-white/40 mb-1">{language === 'es' ? 'Precios Validos' : 'Valid Prices'}</div>
              <div className="text-lg font-mono font-bold text-[#10B981]">{ps.valid_cost_prices}</div>
            </div>
            <div>
              <div className="text-xs text-white/40 mb-1">{language === 'es' ? 'Precios Invalidos' : 'Invalid Prices'}</div>
              <div className={`text-lg font-mono font-bold ${ps.invalid_cost_prices > 0 ? 'text-[#EF4444]' : 'text-white/30'}`}>{ps.invalid_cost_prices}</div>
            </div>
            <div>
              <div className="text-xs text-white/40 mb-1">{language === 'es' ? 'Costo Min.' : 'Min Cost'}</div>
              <div className="text-lg font-mono font-bold text-white/80">${ps.min_cost?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-white/40 mb-1">{language === 'es' ? 'Costo Max.' : 'Max Cost'}</div>
              <div className="text-lg font-mono font-bold text-white/80">${ps.max_cost?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-xs text-white/40 mb-1">{language === 'es' ? 'Costo Prom.' : 'Avg Cost'}</div>
              <div className="text-lg font-mono font-bold text-[#00D4FF]">${ps.avg_cost?.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Error Report */}
      {result.error_details?.length > 0 && (
        <div className="bg-[#050505] border border-[#EF4444]/20 p-5" data-testid="error-details">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#EF4444]">{t('adminImport.errorDetails')} ({result.error_details.length})</h3>
            <button
              onClick={handleDownloadErrorReport}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-xs hover:bg-[#EF4444]/20"
              data-testid="download-error-report-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Download Error Report (CSV)
            </button>
          </div>
          <button onClick={() => setShowErrors(!showErrors)} className="text-xs text-white/40 hover:text-white/60 mb-2" data-testid="toggle-result-errors">
            {showErrors ? 'Collapse' : 'Expand'} error details
          </button>
          {showErrors && (
            <div className="max-h-80 overflow-y-auto">
              <div className="grid grid-cols-[60px_1fr_1fr_2fr] gap-2 text-[10px] text-white/30 uppercase font-semibold pb-1 border-b border-white/5 sticky top-0 bg-[#050505]">
                <span>Row</span><span>Column</span><span>Value</span><span>Reason</span>
              </div>
              {result.error_details.map((e, i) => (
                <div key={i} className="grid grid-cols-[60px_1fr_1fr_2fr] gap-2 text-xs text-white/60 py-1 border-b border-white/[0.03]">
                  <span className="font-mono text-white/30">{e.row}</span>
                  <span className="text-white/50 truncate">{e.column || '\u2014'}</span>
                  <span className="text-[#F59E0B] truncate font-mono">{e.value || '\u2014'}</span>
                  <span className="text-[#EF4444]">{e.reason || e.error || '\u2014'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Review Queue ---
const ReviewQueue = ({ queue, loading, pendingCount, onApprove, onReject, onApproveAll, onRefresh, t }) => (
  <div className="card-dark p-8" data-testid="review-queue">
    <div className="flex items-center justify-between mb-6">
      <div>
        <h2 className="text-xl font-semibold">{t('adminImport.reviewQueueTitle')}</h2>
        <p className="text-white/40 text-sm mt-1">
          {pendingCount > 0
            ? `${pendingCount} ${t('adminImport.productsReview')}${queue.length < pendingCount ? ` (${t('adminImport.showing')} ${queue.length})` : ''}`
            : `0 ${t('adminImport.productsReview')}`
          }
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onRefresh} className="btn-secondary !px-3 !py-2 text-xs" data-testid="refresh-queue-btn">
          <RotateCcw className="w-4 h-4" />
        </button>
        {pendingCount > 0 && (
          <button onClick={onApproveAll} className="btn-primary !py-2 text-xs" data-testid="approve-all-btn">
            {t('adminImport.approveAll')} ({pendingCount})
          </button>
        )}
      </div>
    </div>
    {loading ? (
      <div className="text-center py-8 text-white/40">{t('common.loading')}</div>
    ) : pendingCount === 0 ? (
      <div className="text-center py-8">
        <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
        <p className="text-white/60">{t('adminImport.allReviewed')}</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full table-terminal">
          <thead>
            <tr>
              <th className="text-left">{t('adminImport.skuCol')}</th>
              <th className="text-left">{t('adminImport.brandCol')}</th>
              <th className="text-left">{t('adminImport.modelCol')}</th>
              <th className="text-right">{t('adminImport.costCol')}</th>
              <th className="text-right">{t('adminImport.saleCol')}</th>
              <th className="text-center">{t('adminImport.gradeCol')}</th>
              <th className="text-left">{t('adminImport.errorCol')}</th>
              <th className="text-right">{t('adminImport.actionsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {queue.map((product) => (
              <tr key={product.id} data-testid={`review-row-${product.id}`}>
                <td className="font-mono text-xs text-white/50">{product.unique_id || '\u2014'}</td>
                <td className="text-[#00D4FF]">{product.oem}</td>
                <td className="max-w-[200px] truncate">{product.model}</td>
                <td className="text-right font-mono text-white/50">${product.cost_price?.toLocaleString()}</td>
                <td className="text-right font-mono text-[#00D4FF]">${product.sale_price?.toLocaleString()}</td>
                <td className="text-center">{product.grade || '\u2014'}</td>
                <td>
                  {product.import_error && (
                    <span className="text-xs text-[#EF4444]">{product.import_error}</span>
                  )}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onApprove(product.id)}
                      className="p-2 text-[#10B981] hover:bg-[#10B981]/10"
                      title={t('adminProducts.approve')}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onReject(product.id)}
                      className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10"
                      title={t('adminProducts.reject')}
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// --- Main Component ---
export const AdminImport = () => {
  const { t, language } = useApp();

  const STEPS = [
    { id: 'upload', label: t('adminImport.uploadFile'), icon: Upload },
    { id: 'sheet', label: t('adminImport.selectSheet'), icon: Table2 },
    { id: 'mapping', label: t('adminImport.mapColumns'), icon: Columns3 },
    { id: 'preview', label: t('adminImport.preview'), icon: Eye },
    { id: 'execute', label: t('adminImport.importLabel'), icon: Play },
  ];

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [mappings, setMappings] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importCategory, setImportCategory] = useState('');
  const [presets, setPresets] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [latestBatchId, setLatestBatchId] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [replaceCatalog, setReplaceCatalog] = useState(false);

  const fetchReviewQueue = useCallback(async () => {
    try {
      setLoadingQueue(true);
      const res = await getReviewQueue();
      const data = res.data;
      setReviewQueue(data.products || data || []);
    } catch (err) {
      console.error('Failed to fetch review queue:', err);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await getLatestPendingCount();
      setPendingCount(res.data.pending_count || 0);
      setLatestBatchId(res.data.latest_batch_id || null);
    } catch (err) {
      console.error('Failed to fetch pending count:', err);
    }
  }, []);

  const fetchPresets = useCallback(async () => {
    try {
      const res = await getMappingPresets();
      setPresets(res.data);
    } catch (err) {
      console.error('Failed to fetch presets:', err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await getImportHistory();
      setImportHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch import history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchReviewQueue();
    fetchPendingCount();
    fetchPresets();
    fetchHistory();
  }, [fetchReviewQueue, fetchPendingCount, fetchPresets, fetchHistory]);

  const MAX_FILE_SIZE_MB = 50;

  const handleFileSelect = async (f) => {
    // File size check
    const sizeMB = f.size / (1024 * 1024);
    if (sizeMB > MAX_FILE_SIZE_MB) {
      toast.error(`File too large (${sizeMB.toFixed(1)} MB). Maximum supported: ${MAX_FILE_SIZE_MB} MB`);
      return;
    }
    if (sizeMB > 10) {
      toast.warning(`Large file (${sizeMB.toFixed(1)} MB). Upload may take a moment.`);
    }
    setFile(f);
    setAnalyzing(true);
    try {
      const res = await analyzeImportFile(f);
      const data = res.data;
      setAnalysisData(data);
      if (data.recommended_sheet) setSelectedSheet(data.recommended_sheet);
      if (data.suggested_mappings && Object.keys(data.suggested_mappings).length > 0) setMappings(data.suggested_mappings);

      // Show ignored sheet notes
      if (data.ignored_sheets?.length > 0) {
        data.ignored_sheets.forEach(s => toast.info(`Skipped "${s.name}" — ${s.reason}`));
      }

      if (data.sheets.length === 1) {
        setSelectedSheet(data.sheets[0].name);
        setStep(2);
      } else {
        setStep(1);
      }
      const sheetLabel = data.sheets.length === 1 ? 'sheet' : 'sheets';
      const ignoredCount = data.ignored_sheets?.length || 0;
      const msg = `${t('adminImport.fileAnalyzed')}: ${data.sheets.length} data ${sheetLabel}${ignoredCount > 0 ? ` (${ignoredCount} skipped)` : ''}`;
      toast.success(msg);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 413) {
        toast.error('Upload rejected: file exceeds server size limit. Try a smaller file or CSV format.');
      } else if (status === 504 || err.code === 'ECONNABORTED') {
        toast.error('Upload timed out. The file may be too large or the server is busy. Try again or use CSV format.');
      } else {
        toast.error(detail || t('adminImport.failedAnalyze'));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSheetSelect = (sheetName) => {
    setSelectedSheet(sheetName);
    if (analysisData) {
      const sheet = analysisData.sheets.find((s) => s.name === sheetName);
      if (sheet) {
        const sheetCols = new Set(sheet.columns);
        const cleaned = {};
        for (const [field, col] of Object.entries(mappings)) {
          if (sheetCols.has(col)) cleaned[field] = col;
        }
        setMappings(cleaned);
      }
    }
  };

  const handlePreview = async () => {
    const mappingsList = Object.entries(mappings).map(([field, col]) => ({ source_column: col, target_field: field }));
    if (!mappings.unique_id) { toast.error(t('adminImport.uniqueIdRequired')); return; }
    if (!mappings.cost_price && !mappings.sale_price_override) { toast.error(t('adminImport.costOrSaleRequired')); return; }
    setLoading(true);
    try {
      const res = await previewImport({ session_id: analysisData.session_id, sheet_name: selectedSheet, mappings: mappingsList });
      if (!res.data.valid) { toast.error(res.data.error); return; }
      setPreviewData(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('adminImport.previewFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    const mappingsList = Object.entries(mappings).map(([field, col]) => ({ source_column: col, target_field: field }));
    setLoading(true);
    try {
      const res = await executeImport({ session_id: analysisData.session_id, sheet_name: selectedSheet, mappings: mappingsList, category: importCategory || null, replace_catalog: replaceCatalog });
      if (res.data.expired) {
        toast.error('Session expired. Please re-upload the file.');
        setStep(0);
        return;
      }
      setImportResult(res.data);
      setStep(4);
      fetchReviewQueue();
      fetchPendingCount();
      fetchHistory();
      const added = res.data.imported || 0;
      const upd = res.data.updated || 0;
      const errs = res.data.errors || 0;
      if (errs > 0 && added === 0 && upd === 0) {
        toast.error(`Import failed: all ${errs} rows had validation errors. Check error report.`);
      } else if (errs > 0) {
        toast.warning(`Partial import: ${added} added, ${upd} updated, ${errs} errors`);
      } else {
        toast.success(`${t('adminImport.imported')}: ${added} | ${t('adminImport.updated')}: ${upd}`);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 504 || err.code === 'ECONNABORTED') {
        toast.error('Import timed out. The import may still be running in the background. Check import history in a minute.');
      } else if (detail) {
        toast.error(`Import failed: ${detail}`);
      } else {
        toast.error(t('adminImport.importFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreset = async (name) => {
    const mappingsList = Object.entries(mappings).map(([field, col]) => ({ source_column: col, target_field: field }));
    try {
      await saveMappingPreset({ name, mappings: mappingsList });
      toast.success(`${t('adminImport.presetSaved')}: "${name}"`);
      fetchPresets();
    } catch (err) {
      toast.error(t('adminImport.failedSavePreset'));
    }
  };

  const handleLoadPreset = (preset) => {
    const loaded = {};
    const currentCols = getCurrentColumns();
    for (const m of preset.mappings) {
      if (currentCols.has(m.source_column)) loaded[m.target_field] = m.source_column;
    }
    if (Object.keys(loaded).length === 0) { toast.error(t('adminImport.noMatchingColumns')); return; }
    setMappings(loaded);
    toast.success(`${t('adminImport.presetLoaded')}: "${preset.name}" \u2014 ${Object.keys(loaded).length} ${t('adminImport.mappingsApplied')}`);
  };

  const handleDeletePreset = async (id) => {
    try {
      await deleteMappingPreset(id);
      toast.success(t('adminImport.presetDeleted'));
      fetchPresets();
    } catch (err) {
      toast.error(t('adminImport.failedDeletePreset'));
    }
  };

  const handleDeleteHistory = async (id) => {
    if (!window.confirm(t('adminImport.deleteLogConfirm'))) return;
    try {
      await deleteImportHistory(id);
      toast.success(t('adminImport.importLogDeleted'));
      fetchHistory();
    } catch (err) {
      toast.error(t('adminImport.failedDeleteLog'));
    }
  };

  const handleDownloadFile = (id) => {
    const token = localStorage.getItem('luxortek_admin_token');
    const url = getImportFileDownloadUrl(id);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Download failed');
        const filename = res.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || 'download';
        return res.blob().then(blob => ({ blob, filename }));
      })
      .then(({ blob, filename }) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error(t('adminImport.downloadFailed')));
  };

  const handleApprove = async (id) => {
    try { await approveProduct(id); toast.success(t('adminImport.productApproved')); fetchReviewQueue(); fetchPendingCount(); }
    catch (err) { toast.error(t('adminImport.failedApprove')); }
  };

  const handleReject = async (id) => {
    try { await rejectProduct(id); toast.success(t('adminImport.productRejected')); fetchReviewQueue(); fetchPendingCount(); }
    catch (err) { toast.error(t('adminImport.failedReject')); }
  };

  const handleApproveAll = async () => {
    if (!window.confirm(`${t('adminImport.confirmApproveAll')} (${pendingCount})`)) return;
    try {
      const res = await approveAllLatestBatch();
      const count = res.data.approved_count || 0;
      toast.success(`${t('adminImport.allApproved')} (${count})`);
      fetchReviewQueue();
      fetchPendingCount();
    } catch (err) {
      toast.error(t('adminImport.failedApproveAll'));
    }
  };

  const resetWizard = () => {
    setStep(0); setFile(null); setAnalysisData(null); setSelectedSheet('');
    setMappings({}); setPreviewData(null); setImportResult(null); setImportCategory(''); setReplaceCatalog(false);
  };

  const getCurrentColumns = () => {
    if (!analysisData || !selectedSheet) return new Set();
    const sheet = analysisData.sheets.find((s) => s.name === selectedSheet);
    return new Set(sheet?.columns || []);
  };

  const getSelectedSheetColumns = () => {
    if (!analysisData || !selectedSheet) return [];
    const sheet = analysisData.sheets.find((s) => s.name === selectedSheet);
    return sheet?.columns || [];
  };

  const canGoNext = () => {
    switch (step) {
      case 0: return !!file;
      case 1: return !!selectedSheet;
      case 2: return !!mappings.unique_id && (!!mappings.cost_price || !!mappings.sale_price_override);
      case 3: return previewData?.valid;
      default: return false;
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-import">
      <Toaster position="top-right" theme="dark" />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Upload className="w-8 h-8 text-[#00D4FF]" />
          {t('adminImport.title')}
        </h1>
        <p className="text-white/60 mt-1">{t('adminImport.subtitle')}</p>
      </div>

      {/* Wizard */}
      <div className="card-dark p-8 mb-8">
        <StepIndicator currentStep={step} steps={STEPS} />

        {(analyzing || loading) && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#00D4FF] animate-spin" />
            <span className="ml-3 text-white/60">
              {analyzing ? t('adminImport.analyzingFile') : t('adminImport.processingImport')}
            </span>
          </div>
        )}

        {!analyzing && !loading && (
          <>
            {step === 0 && <UploadStep file={file} onFileSelect={handleFileSelect} t={t} />}
            {step === 1 && analysisData && (
              <SheetStep sheets={analysisData.sheets} selectedSheet={selectedSheet} onSelect={handleSheetSelect}
                recommended={analysisData.recommended_sheet} ignoredSheets={analysisData.ignored_sheets} sheetNotes={analysisData.sheet_notes} t={t} />
            )}
            {step === 2 && analysisData && (
              <MappingStep columns={getSelectedSheetColumns()} systemFields={analysisData.system_fields} mappings={mappings}
                onMappingsChange={setMappings} presets={presets} onSavePreset={handleSavePreset} onLoadPreset={handleLoadPreset}
                onDeletePreset={handleDeletePreset} importCategory={importCategory} onCategoryChange={setImportCategory} t={t} />
            )}
            {step === 3 && <PreviewStep previewData={previewData?.preview} totalRows={previewData?.total_rows} mappedFields={previewData?.mapped_fields || []} validation={previewData?.validation} t={t} />}
            {step === 3 && (
              <div className="mt-6 p-4 bg-[#050505] border border-white/10 flex items-center gap-4" data-testid="category-selector">
                <label className="text-sm text-white/60 shrink-0">{t('adminImport.tagCategory')}</label>
                <select value={importCategory} onChange={(e) => setImportCategory(e.target.value)}
                  className="text-sm bg-black/50 border border-white/10 text-white px-3 py-2 h-auto" data-testid="import-category-select">
                  <option value="">{t('adminImport.selectCategory')}</option>
                  {CATEGORY_PRESETS.map((c) => (<option key={c} value={c}>{c}</option>))}
                </select>
                <span className="text-xs text-white/30">{t('adminImport.categoryTrackingHint')}</span>
              </div>
            )}
            {step === 3 && (
              <div className="mt-3 p-4 bg-[#050505] border border-white/10 flex items-center gap-4" data-testid="replace-catalog-toggle">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={replaceCatalog}
                    onChange={(e) => setReplaceCatalog(e.target.checked)}
                    className="w-4 h-4 accent-[#EF4444]"
                    data-testid="replace-catalog-checkbox"
                  />
                  <span className="text-sm text-white/80 font-medium">{t('adminImport.replaceCatalog')}</span>
                </label>
                <span className="text-xs text-white/30">{t('adminImport.replaceCatalogHint')}</span>
              </div>
            )}
            {step === 4 && <ResultStep result={importResult} t={t} language={language} />}
          </>
        )}

        {/* Navigation */}
        {!analyzing && !loading && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <div>
              {step > 0 && step < 4 && (
                <button onClick={() => setStep(step - 1)} className="btn-secondary !py-2 flex items-center gap-2" data-testid="wizard-back">
                  <ChevronLeft className="w-4 h-4" /> {t('adminImport.back')}
                </button>
              )}
              {step === 4 && (
                <button onClick={resetWizard} className="btn-secondary !py-2 flex items-center gap-2" data-testid="wizard-reset">
                  <RotateCcw className="w-4 h-4" /> {t('adminImport.importAnotherFile')}
                </button>
              )}
            </div>
            <div>
              {step === 1 && (
                <button onClick={() => setStep(2)} disabled={!canGoNext()} className="btn-primary !py-2 flex items-center gap-2 disabled:opacity-30" data-testid="wizard-next">
                  {t('adminImport.next')} <ChevronRight className="w-4 h-4" />
                </button>
              )}
              {step === 2 && (
                <button onClick={handlePreview} disabled={!canGoNext()} className="btn-primary !py-2 flex items-center gap-2 disabled:opacity-30" data-testid="wizard-preview">
                  <Eye className="w-4 h-4" /> {t('adminImport.previewData')}
                </button>
              )}
              {step === 3 && (
                <button onClick={handleExecute} disabled={!canGoNext()} className="btn-primary !py-2 flex items-center gap-2 disabled:opacity-30" data-testid="wizard-execute">
                  <Play className="w-4 h-4" /> {t('adminImport.executeImport')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Review Queue */}
      <ReviewQueue queue={reviewQueue} loading={loadingQueue} pendingCount={pendingCount} onApprove={handleApprove} onReject={handleReject} onApproveAll={handleApproveAll} onRefresh={() => { fetchReviewQueue(); fetchPendingCount(); }} t={t} />

      {/* Import History */}
      <div className="card-dark p-8 mt-8" data-testid="import-history">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <History className="w-5 h-5 text-[#00D4FF]" />
              {t('adminImport.importHistoryTitle')}
            </h2>
            <p className="text-white/40 text-sm mt-1">{t('adminImport.importHistoryDesc')}</p>
          </div>
          <button onClick={fetchHistory} className="btn-secondary !px-3 !py-2 text-xs" data-testid="refresh-history-btn">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        {loadingHistory ? (
          <div className="text-center py-8 text-white/40">{t('common.loading')}</div>
        ) : importHistory.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-white/10 mx-auto mb-4" />
            <p className="text-white/40">{t('adminImport.noHistory')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-terminal">
              <thead>
                <tr>
                  <th className="text-left">{t('adminImport.dateCol')}</th>
                  <th className="text-left">{t('adminImport.fileCol')}</th>
                  <th className="text-left">{t('adminImport.categoryCol')}</th>
                  <th className="text-center">{t('adminImport.importedCol')}</th>
                  <th className="text-center">{t('adminImport.failedCol')}</th>
                  <th className="text-center">{t('adminImport.statusCol')}</th>
                  <th className="text-left">{t('adminImport.userCol')}</th>
                  <th className="text-right">{t('adminImport.actionsCol')}</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map((entry) => (
                  <tr key={entry.id} data-testid={`history-row-${entry.id}`}>
                    <td className="text-white/50 text-xs whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleDateString(language === 'es' ? 'es' : 'en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </td>
                    <td className="text-white/80 max-w-[180px] truncate" title={entry.filename}>{entry.filename}</td>
                    <td>
                      {entry.category && entry.category !== 'Uncategorized' ? (
                        <span className="text-xs px-2 py-0.5 bg-[#00D4FF]/10 text-[#00D4FF] border border-[#00D4FF]/20">{entry.category}</span>
                      ) : (
                        <span className="text-white/20 text-xs">\u2014</span>
                      )}
                    </td>
                    <td className="text-center font-mono text-[#10B981]">{entry.imported}</td>
                    <td className="text-center font-mono text-[#EF4444]">{entry.errors > 0 ? entry.errors : '\u2014'}</td>
                    <td className="text-center">
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 font-medium ${
                        entry.status === 'success' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        entry.status === 'partial' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                        'bg-[#EF4444]/20 text-[#EF4444]'
                      }`}>
                        {entry.status}
                      </span>
                    </td>
                    <td className="text-white/50 text-xs flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {entry.admin_name || entry.admin_email}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleDownloadFile(entry.id)} className="p-2 text-white/40 hover:text-[#00D4FF] transition-colors" data-testid={`download-${entry.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDeleteHistory(entry.id)} className="p-2 text-white/20 hover:text-[#EF4444] transition-colors" data-testid={`delete-history-${entry.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
